import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { Author, ManuscriptAuthor, AuthorProfile } from './types';
import { ApiService } from './services/api';
import './App.css';

const COUNTRY_OPTIONS = [
  { code: 'all', label: 'Worldwide (no filter)' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'AR', label: 'Argentina' },
  { code: 'AU', label: 'Australia' },
  { code: 'AT', label: 'Austria' },
  { code: 'BE', label: 'Belgium' },
  { code: 'BR', label: 'Brazil' },
  { code: 'CA', label: 'Canada' },
  { code: 'CL', label: 'Chile' },
  { code: 'CN', label: 'China' },
  { code: 'CO', label: 'Colombia' },
  { code: 'CZ', label: 'Czech Republic' },
  { code: 'DK', label: 'Denmark' },
  { code: 'EG', label: 'Egypt' },
  { code: 'FI', label: 'Finland' },
  { code: 'FR', label: 'France' },
  { code: 'DE', label: 'Germany' },
  { code: 'GR', label: 'Greece' },
  { code: 'HK', label: 'Hong Kong' },
  { code: 'HU', label: 'Hungary' },
  { code: 'IN', label: 'India' },
  { code: 'ID', label: 'Indonesia' },
  { code: 'IR', label: 'Iran' },
  { code: 'IE', label: 'Ireland' },
  { code: 'IL', label: 'Israel' },
  { code: 'IT', label: 'Italy' },
  { code: 'JP', label: 'Japan' },
  { code: 'KR', label: 'South Korea' },
  { code: 'MY', label: 'Malaysia' },
  { code: 'MX', label: 'Mexico' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'NG', label: 'Nigeria' },
  { code: 'NO', label: 'Norway' },
  { code: 'PK', label: 'Pakistan' },
  { code: 'PL', label: 'Poland' },
  { code: 'PT', label: 'Portugal' },
  { code: 'RO', label: 'Romania' },
  { code: 'RU', label: 'Russia' },
  { code: 'SA', label: 'Saudi Arabia' },
  { code: 'SG', label: 'Singapore' },
  { code: 'ZA', label: 'South Africa' },
  { code: 'ES', label: 'Spain' },
  { code: 'SE', label: 'Sweden' },
  { code: 'CH', label: 'Switzerland' },
  { code: 'TW', label: 'Taiwan' },
  { code: 'TH', label: 'Thailand' },
  { code: 'TR', label: 'Turkey' },
  { code: 'UA', label: 'Ukraine' },
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'US', label: 'United States' },
  { code: 'VN', label: 'Vietnam' },
];

const exportToExcel = (authors: Author[]) => {
  const rows = authors.map(a => ({
    'Reviewer Name': a.fullName,
    'Institution': a.affiliation,
    'Paper Name': a.paperTitle,
    'Publication Date': a.publicationDate,
    '% Confidence': Math.round(a.confidenceScore * 100),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Reviewers');
  XLSX.writeFile(wb, 'ReviewerResults.xlsx');
};

const App: React.FC = () => {
  const [abstract, setAbstract] = useState('');
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manuscriptAuthors, setManuscriptAuthors] = useState<ManuscriptAuthor[]>([{ name: '', affiliation: '' }]);
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [excludeSameInstitution, setExcludeSameInstitution] = useState(false);
  const [maxPerInstitution, setMaxPerInstitution] = useState<number | null>(null);
  const [isUnlimited, setIsUnlimited] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState<AuthorProfile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const handleManuscriptAuthorChange = (index: number, field: keyof ManuscriptAuthor, value: string) => {
    const newAuthors = [...manuscriptAuthors];
    newAuthors[index] = { ...newAuthors[index], [field]: value };
    setManuscriptAuthors(newAuthors);
  };

  const addManuscriptAuthor = () => {
    setManuscriptAuthors([...manuscriptAuthors, { name: '', affiliation: '' }]);
  };

  const removeManuscriptAuthor = (index: number) => {
    if (manuscriptAuthors.length > 1) {
      const newAuthors = manuscriptAuthors.filter((_, i) => i !== index);
      setManuscriptAuthors(newAuthors);
    }
  };

  const handleViewProfile = async (authorId: string) => {
    if (!authorId) return;
    setIsProfileLoading(true);
    setProfileError(null);
    setSelectedProfile(null);
    try {
      const profile = await ApiService.getAuthorProfile(authorId);
      if (profile) {
        setSelectedProfile(profile);
      } else {
        setProfileError("Could not retrieve author's profile.");
      }
    } catch (err) {
      setProfileError('An error occurred while fetching the profile.');
    } finally {
      setIsProfileLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedProfile(null);
    setProfileError(null);
  };

  const handleSearch = async () => {
    if (!abstract.trim()) {
      setError('Please enter an abstract');
      return;
    }

    setLoading(true);
    setError(null);
    setAuthors([]);

    try {
      const response = await ApiService.searchReviewers(abstract, manuscriptAuthors, locationFilter, excludeSameInstitution, isUnlimited ? null : maxPerInstitution);
      
      if (response.success) {
        setAuthors(response.authors);
        if (response.authors.length === 0) {
          setError('No reviewers found for this abstract. Try different keywords or a longer abstract.');
        }
      } else {
        setError(response.errorMessage || 'Failed to search for reviewers');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <img src="/Flexigrant-Logo.svg" alt="Flexigrant Logo" className="App-logo" />
        <h1>Reviewer Finder</h1>
        <p>Find suitable peer reviewers based on your research abstract</p>
      </header>

      <main className="App-main">
        <div className="search-section">
          <div className="input-group">
            <label htmlFor="abstract">Research Abstract</label>
            <textarea
              id="abstract"
              value={abstract}
              onChange={(e) => setAbstract(e.target.value)}
              placeholder="Paste your research abstract here..."
              rows={8}
              className="abstract-input"
            />
          </div>

          <div className="manuscript-authors-section">
            <label>Manuscript Authors (for Conflict of Interest Check)</label>
            {manuscriptAuthors.map((author, index) => (
              <div key={index} className="manuscript-author-row">
                <input
                  type="text"
                  placeholder="Author Name (e.g., Dr. Ben Carter)"
                  value={author.name}
                  onChange={(e) => handleManuscriptAuthorChange(index, 'name', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Author Affiliation (e.g., Lunar Research Institute)"
                  value={author.affiliation}
                  onChange={(e) => handleManuscriptAuthorChange(index, 'affiliation', e.target.value)}
                />
                <button onClick={() => removeManuscriptAuthor(index)} className="remove-author-btn" disabled={manuscriptAuthors.length <= 1}>
                  &times;
                </button>
              </div>
            ))}
            <button onClick={addManuscriptAuthor} className="add-author-btn">
              + Add Another Author
            </button>
          </div>

          <div className="location-filter-section">
            <label htmlFor="location-filter">Reviewer Location</label>
            <select
              id="location-filter"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="location-select"
            >
              {COUNTRY_OPTIONS.map(opt => (
                <option key={opt.code} value={opt.code}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-card">
            <label className="filter-checkbox-label">
              <input
                type="checkbox"
                checked={excludeSameInstitution}
                onChange={(e) => setExcludeSameInstitution(e.target.checked)}
              />
              <span className="filter-card-label">Exclude reviewers from same institution as authors?</span>
              <span className="new-badge">NEW</span>
            </label>
            <p className="filter-card-description">When checked, reviewers affiliated with the same institution as any listed author will be excluded from results.</p>
          </div>

          <div className="filter-card">
            <div className="filter-card-header">
              <span className="filter-card-label">Max reviewers returned from any institution</span>
              <span className="new-badge">NEW</span>
            </div>
            <p className="filter-card-description">Limit how many reviewers can be returned from a single institution. Leave as unlimited for no cap.</p>
            <div className="max-per-institution-controls">
              <input
                type="number"
                min={1}
                value={isUnlimited ? '' : (maxPerInstitution ?? '')}
                onChange={(e) => {
                  const val = e.target.value ? parseInt(e.target.value, 10) : null;
                  setMaxPerInstitution(val);
                  if (val && val > 0) setIsUnlimited(false);
                }}
                disabled={isUnlimited}
                className="max-per-institution-input"
              />
              <label className="unlimited-checkbox-label">
                <input
                  type="checkbox"
                  checked={isUnlimited}
                  onChange={(e) => {
                    setIsUnlimited(e.target.checked);
                    if (e.target.checked) setMaxPerInstitution(null);
                  }}
                />
                Unlimited
              </label>
            </div>
          </div>

          <button
            onClick={handleSearch}
            disabled={loading || !abstract.trim()}
            className="search-button"
          >
            {loading ? 'Searching...' : 'Find Reviewers'}
          </button>
        </div>

        {error && (
          <div className="error-message">
            <p>❌ {error}</p>
          </div>
        )}

        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p>Searching for relevant reviewers...</p>
          </div>
        )}

        {authors.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h2>Potential Reviewers ({authors.length})</h2>
              <button onClick={() => exportToExcel(authors)} className="export-button">
                Export to Excel
              </button>
            </div>
            <div className="authors-grid">
              {authors.map((author, index) => (
                <div key={index} className="author-card">
                  <div className="author-header">
                    <h3>{author.fullName}</h3>
                    <span className="confidence-score">
                      {Math.round(author.confidenceScore * 100)}% confidence
                    </span>
                  </div>
                  <p className="affiliation">🏛️ {author.affiliation}</p>
                  {author.rawAffiliation && (
                    <p className="raw-affiliation">🎓 {author.rawAffiliation}</p>
                  )}
                  <p className="paper-title">📄 {author.paperTitle}</p>
                  <p className="publication-date">📅 {author.publicationDate}</p>
                  {author.doi && (
                    <p className="doi">
                      DOI: <a href={author.doi} target="_blank" rel="noopener noreferrer">{author.doi}</a>
                    </p>
                  )}
                  {author.email && (
                    <p className="email">✉️ {author.email}</p>
                  )}
                  {author.conflictOfInterestReason && (
                    <div className="coi-warning">
                      ⚠️ **Conflict of Interest:** {author.conflictOfInterestReason}
                    </div>
                  )}
                  
                  <div className="author-links">
                    {author.authorProfileUrl && (
                      <a 
                        href={author.authorProfileUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="link-button author-link"
                        title="View author profile"
                      >
                        👤 Author Profile
                      </a>
                    )}
                    {author.paperUrl && (
                      <a 
                        href={author.paperUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="link-button paper-link"
                        title="View paper details"
                      >
                        📖 View Paper
                      </a>
                    )}
                    {author.authorOpenAlexId && (
                      <button
                        onClick={() => handleViewProfile(author.authorOpenAlexId!)}
                        className="link-button profile-button"
                        title="View detailed profile"
                      >
                        🔎 Deeper Analysis
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(selectedProfile || isProfileLoading || profileError) && (
          <ProfileModal
            profile={selectedProfile}
            isLoading={isProfileLoading}
            error={profileError}
            onClose={closeModal}
          />
        )}
      </main>

      <footer className="App-footer">
        <p>Powered by OpenAlex • No data is stored • GDPR compliant</p>
      </footer>
    </div>
  );
};

interface ProfileModalProps {
  profile: AuthorProfile | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ profile, isLoading, error, onClose }) => {
  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <button onClick={onClose} className="modal-close-btn">&times;</button>
        {isLoading && <div className="spinner"></div>}
        {error && <div className="error-message">{error}</div>}
        {profile && (
          <>
            <h2>{profile.fullName}</h2>
            <p className="affiliation">🏛️ {profile.affiliation}</p>
            <div className="profile-stats">
              <span><strong>Works:</strong> {profile.worksCount}</span>
              <span><strong>Citations:</strong> {profile.citedByCount}</span>
            </div>

            <div className="profile-section">
              <h3>Top Research Concepts</h3>
              {profile.concepts && profile.concepts.length > 0 ? (
                <ul className="concepts-list">
                  {profile.concepts.slice(0, 5).map(concept => (
                    <li key={concept.displayName}>
                      {concept.displayName} <span className="score">({(concept.score * 100).toFixed(1)}%)</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No concept data available.</p>
              )}
            </div>

            <div className="profile-section">
              <h3>Recent Publications</h3>
              {profile.recentWorks && profile.recentWorks.length > 0 ? (
                <ul className="works-list">
                  {profile.recentWorks.map(work => (
                    <li key={work.doi || work.title}>
                      <p><strong>{work.title}</strong> ({work.publicationYear ?? 'N/A'})</p>
                      {work.doi && <a href={work.doi} target="_blank" rel="noopener noreferrer">View Paper</a>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No publications available.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default App; 